import React, { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowBack,
  ClosedCaptionOutlined,
  Forward10,
  Fullscreen,
  FullscreenExit,
  Pause,
  PictureInPictureAlt,
  PlayArrow,
  Replay10,
  SettingsOutlined,
  SkipNext,
  VolumeOff,
  VolumeUp,
} from "@mui/icons-material";
import "./Video.scss";
import { useAuth } from "../context/AuthContext";
import { saveProgress } from "../services/apiService";
import {
  buildMediaUrl,
  fetchPlaybackContext,
  getDirectPlayPath,
} from "../services/streamApi";
import LikeDislike from "./LikeDislike";

const CONTROL_TIMEOUT = 2600;
const AUTOPLAY_COUNTDOWN = 8;

function getProgressStorageKey(contentId, episodeId) {
  return `streamflix:progress:${contentId}:${episodeId || "feature"}`;
}

function readStoredProgress(contentId, episodeId) {
  try {
    const raw = localStorage.getItem(getProgressStorageKey(contentId, episodeId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredProgress(contentId, episodeId, progress) {
  try {
    localStorage.setItem(getProgressStorageKey(contentId, episodeId), JSON.stringify(progress));
  } catch {
    // ignore storage errors
  }
}

function formatTime(seconds) {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = Math.floor(safe % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

function VideoPlayer() {
  const { id, episodeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);
  const hideControlsRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const syncedOnceRef = useRef(false);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState({ primary: "", fallback: "" });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(-1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [selectedCaption, setSelectedCaption] = useState(-1);
  const [countdown, setCountdown] = useState(AUTOPLAY_COUNTDOWN);
  const [showNextPanel, setShowNextPanel] = useState(false);

  const title = payload?.title || null;
  const activeAsset = payload?.activeAsset || null;
  const seasons = payload?.seasons || [];
  const nextPlayable = payload?.nextPlayable || null;
  const recommendations = payload?.recommendations || [];
  const captionTracks = useMemo(
    () => activeAsset?.captionTracks || title?.captionTracks || [],
    [activeAsset?.captionTracks, title?.captionTracks]
  );

  const activeMarkers = activeAsset?.markers || {};
  const showSkipRecap =
    Boolean(activeMarkers.recap) &&
    position >= activeMarkers.recap.start &&
    position <= activeMarkers.recap.end;
  const showSkipIntro =
    Boolean(activeMarkers.intro) &&
    position >= activeMarkers.intro.start &&
    position <= activeMarkers.intro.end;

  const shouldPromptNext =
    Boolean(nextPlayable && activeMarkers.credits) && position >= activeMarkers.credits.start;

  const activeEpisodeLabel = useMemo(() => {
    if (activeAsset?.seasonNumber && activeAsset?.episodeNumber) {
      return `S${activeAsset.seasonNumber}:E${activeAsset.episodeNumber}`;
    }
    return null;
  }, [activeAsset?.episodeNumber, activeAsset?.seasonNumber]);

  const captionLabels = useMemo(
    () => captionTracks.map((track) => track.label || track.language).filter(Boolean),
    [captionTracks]
  );

  const qualityLabels = useMemo(() => {
    const hlsLevels = levels
      .map((level) => (level.height ? `${level.height}p` : null))
      .filter(Boolean);
    const configuredQualities = [
      ...(activeAsset?.qualities || []),
      ...(title?.qualities || []),
      ...(activeAsset?.versions || []),
      ...(title?.versions || []),
    ]
      .map((quality) => {
        if (typeof quality === "string") return quality;
        return quality?.label || quality?.quality || quality?.height;
      })
      .filter(Boolean)
      .map((quality) => (String(quality).endsWith("p") ? String(quality) : `${quality}p`));

    return Array.from(new Set([...hlsLevels, ...configuredQualities]));
  }, [activeAsset?.qualities, activeAsset?.versions, levels, title?.qualities, title?.versions]);

  const detailFacts = useMemo(
    () =>
      [
        { label: "Type", value: title?.type === "film" ? "Film" : title?.type === "serie" ? "Serie" : title?.type },
        { label: "Langue", value: activeAsset?.audio || title?.audio },
        { label: "Sous-titres", value: captionLabels.length ? captionLabels.join(", ") : null },
        { label: "Classification", value: activeAsset?.maturity || title?.maturity },
        { label: "Qualite", value: qualityLabels.length ? qualityLabels.join(" / ") : null },
      ].filter((fact) => fact.value),
    [
      activeAsset?.audio,
      activeAsset?.maturity,
      captionLabels,
      qualityLabels,
      title?.audio,
      title?.maturity,
      title?.type,
    ]
  );

  useEffect(() => {
    let active = true;

    async function loadPlayback() {
      try {
        setLoading(true);
        syncedOnceRef.current = false;
        const data = await fetchPlaybackContext({
          id,
          userId: user?.userId,
          episodeId,
        });

        if (!active) return;

        setPayload(data);
        setSource({
          primary: data?.activeAsset?.hlsPath ? buildMediaUrl(data.activeAsset.hlsPath) : "",
          fallback: data?.activeAsset?.videoPath ? buildMediaUrl(data.activeAsset.videoPath) : "",
        });
      } catch (error) {
        console.error("Erreur lecture:", error);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadPlayback();
    return () => {
      active = false;
    };
  }, [episodeId, id, user?.userId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || (!source.primary && !source.fallback)) {
      return undefined;
    }

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const restorePosition = () => {
      const localProgress = readStoredProgress(id, episodeId);
      const resumePosition = localProgress?.positionSeconds ?? title?.resume?.positionSeconds ?? 0;
      const targetTime = Number(resumePosition);

      if (Number.isFinite(targetTime) && targetTime > 15 && video.duration && targetTime < video.duration - 25) {
        video.currentTime = targetTime;
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration || activeAsset?.duration || 0);
      restorePosition();
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    const attachNativeSource = (path) => {
      if (!path) return;
      video.src = path;
      video.load();
    };

    if (source.primary && source.primary.endsWith(".m3u8") && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });

      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(source.primary);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        setLevels(data.levels || []);
      });
      hls.on(Hls.Events.LEVEL_SWITCHED, () => {
        setSelectedLevel(hls.currentLevel);
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal && source.fallback && source.fallback !== source.primary) {
          hls.destroy();
          hlsRef.current = null;
          attachNativeSource(source.fallback);
        }
      });
    } else {
      attachNativeSource(source.primary || source.fallback);
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [activeAsset?.duration, episodeId, id, source.fallback, source.primary, title?.resume?.positionSeconds]);

  useEffect(() => {
    setShowNextPanel(shouldPromptNext);
  }, [shouldPromptNext]);

  useEffect(() => {
    const defaultIndex = captionTracks.findIndex((track) => track.isDefault);
    setSelectedCaption(defaultIndex >= 0 ? defaultIndex : -1);
  }, [activeAsset?.id, captionTracks]);

  useEffect(() => {
    if (!showNextPanel || !nextPlayable) {
      setCountdown(AUTOPLAY_COUNTDOWN);
      return undefined;
    }

    setCountdown(nextPlayable.countdownSeconds || AUTOPLAY_COUNTDOWN);

    const timer = window.setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          navigate(getDirectPlayPath(title, nextPlayable.episodeId));
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [navigate, nextPlayable, showNextPanel, title]);

  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) {
        window.clearInterval(syncIntervalRef.current);
      }
      if (hideControlsRef.current) {
        window.clearTimeout(hideControlsRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const syncTracks = () => {
      const tracks = Array.from(video.textTracks || []);
      tracks.forEach((track, index) => {
        track.mode = index === selectedCaption ? "showing" : "disabled";
      });
    };

    const timer = window.setTimeout(syncTracks, 80);
    video.addEventListener("loadedmetadata", syncTracks);

    return () => {
      window.clearTimeout(timer);
      video.removeEventListener("loadedmetadata", syncTracks);
    };
  }, [captionTracks, selectedCaption, source.fallback, source.primary]);

  const scheduleHideControls = () => {
    setShowControls(true);
    if (hideControlsRef.current) {
      window.clearTimeout(hideControlsRef.current);
    }

    hideControlsRef.current = window.setTimeout(() => {
      if (!videoRef.current?.paused) {
        setShowControls(false);
      }
    }, CONTROL_TIMEOUT);
  };

  const persistProgress = async () => {
    const video = videoRef.current;
    if (!video || !title?.id) return;

    const payloadToSave = {
      positionSeconds: Math.floor(video.currentTime || 0),
      durationSeconds: Math.floor(video.duration || activeAsset?.duration || 0),
      updatedAt: Date.now(),
    };

    writeStoredProgress(id, episodeId, payloadToSave);

    if (user?.userId) {
      await saveProgress({
        contentId: title.id,
        userId: user.userId,
        positionSeconds: payloadToSave.positionSeconds,
        durationSeconds: payloadToSave.durationSeconds,
      });
    }
  };

  const startSyncLoop = () => {
    if (syncIntervalRef.current) return;
    syncIntervalRef.current = window.setInterval(() => {
      if (!videoRef.current?.paused) {
        persistProgress();
      }
    }, 15000);
  };

  const stopSyncLoop = () => {
    if (syncIntervalRef.current) {
      window.clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  };

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      await video.play();
      if (!syncedOnceRef.current) {
        persistProgress();
        syncedOnceRef.current = true;
      }
      startSyncLoop();
      scheduleHideControls();
    } else {
      video.pause();
      stopSyncLoop();
      persistProgress();
      setShowControls(true);
    }
  };

  const handleProgress = () => {
    const video = videoRef.current;
    if (!video) return;

    setPosition(video.currentTime || 0);
    setDuration(video.duration || activeAsset?.duration || 0);
    setProgress(video.duration ? (video.currentTime / video.duration) * 100 : 0);
  };

  const handleSeek = (event) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const nextTime = (Number(event.target.value) / 100) * duration;
    video.currentTime = nextTime;
    setPosition(nextTime);
    setProgress(Number(event.target.value));
  };

  const changePlaybackRate = (rate) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setPlaybackRate(rate);
  };

  const onQualitySelect = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!hlsRef.current) return;
    hlsRef.current.currentLevel = value;
    setSelectedLevel(value);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !isMuted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = async () => {
    if (!playerRef.current) return;

    if (!document.fullscreenElement) {
      await playerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const jumpToNext = () => {
    if (nextPlayable) {
      navigate(getDirectPlayPath(title, nextPlayable.episodeId));
    }
  };

  const skipWindow = (marker) => {
    const video = videoRef.current;
    if (!video || !marker?.end) return;
    video.currentTime = marker.end;
  };

  const visibleEpisodes = seasons.flatMap((season) => season.episodes || []).slice(0, 6);

  return (
    <div className="player-page">
      <div
        ref={playerRef}
        className="player-page__frame"
        onMouseMove={scheduleHideControls}
        onMouseLeave={() => !videoRef.current?.paused && setShowControls(false)}
      >
        <video
          ref={videoRef}
          onTimeUpdate={handleProgress}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            persistProgress();
            stopSyncLoop();
            if (nextPlayable) {
              jumpToNext();
            }
          }}
          className="video-player"
          poster={buildMediaUrl(activeAsset?.imagePath || title?.backdropPath || title?.posterPath)}
        >
          {captionTracks.map((track) => (
            <track
              key={track.id || `${track.language}-${track.label}`}
              kind={track.kind === "captions" ? "captions" : "subtitles"}
              src={buildMediaUrl(track.filePath)}
              srcLang={track.language}
              label={track.label}
              default={track.isDefault}
            />
          ))}
        </video>

        {showControls ? (
          <>
            <div className="player-page__topBar">
              <button className="player-page__ghost" onClick={() => navigate(-1)}>
                <ArrowBack />
              </button>
              <div className="player-page__titleBlock">
                <p>{activeEpisodeLabel || title?.type}</p>
                <h1>{activeAsset?.title || title?.title || "Lecture"}</h1>
              </div>
            </div>

            <div className="center-controls">
              <button className="left-skip" onClick={() => (videoRef.current.currentTime -= 10)}>
                <Replay10 />
              </button>

              <button className="main-play" onClick={togglePlay}>
                {isPlaying ? <Pause /> : <PlayArrow />}
              </button>

              <button className="right-skip" onClick={() => (videoRef.current.currentTime += 10)}>
                <Forward10 />
              </button>
            </div>

            <div className="controls-overlay">
              <input
                type="range"
                className="progress-bar"
                min="0"
                max="100"
                value={progress}
                onChange={handleSeek}
              />

              <div className="controls-bar">
                <div className="controls-left">
                  <button onClick={togglePlay}>{isPlaying ? <Pause /> : <PlayArrow />}</button>

                  <button onClick={toggleMute}>{isMuted ? <VolumeOff /> : <VolumeUp />}</button>

                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={(event) => {
                      const nextVolume = parseFloat(event.target.value);
                      setVolume(nextVolume);
                      if (videoRef.current) {
                        videoRef.current.volume = nextVolume;
                        videoRef.current.muted = nextVolume === 0;
                      }
                      setIsMuted(nextVolume === 0);
                    }}
                    className="volume-bar"
                  />

                  <span className="time">
                    {formatTime(position)} / {formatTime(duration)}
                  </span>
                </div>

                <div className="controls-right">
                  <select value={playbackRate} onChange={(event) => changePlaybackRate(Number(event.target.value))} className="playbackRate">
                    <option value={0.75}>0.75x</option>
                    <option value={1}>1x</option>
                    <option value={1.25}>1.25x</option>
                    <option value={1.5}>1.5x</option>
                    <option value={2}>2x</option>
                  </select>

                  {levels.length ? (
                    <select value={selectedLevel} onChange={onQualitySelect} title="Qualite" className="selectedLevel">
                      <option value={-1}>Auto</option>
                      {levels.map((level, index) => (
                        <option key={index} value={index}>
                          {(level.height || "Auto").toString()}p
                        </option>
                      ))}
                    </select>
                  ) : null}

                  {captionTracks.length ? (
                    <select
                      value={selectedCaption}
                      onChange={(event) => setSelectedCaption(Number(event.target.value))}
                      title="Sous-titres"
                      className="selectedLevel"
                    >
                      <option value={-1}>Sous-titres off</option>
                      {captionTracks.map((track, index) => (
                        <option key={track.id || `${track.language}-${index}`} value={index}>
                          {track.label}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  <button onClick={() => videoRef.current?.requestPictureInPicture()}>
                    <PictureInPictureAlt />
                  </button>

                  <button onClick={toggleFullscreen}>{isFullscreen ? <FullscreenExit /> : <Fullscreen />}</button>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {showSkipRecap ? (
          <button className="player-page__skip player-page__skip--recap" onClick={() => skipWindow(activeMarkers.recap)}>
            Passer le recap
          </button>
        ) : null}

        {showSkipIntro ? (
          <button className="player-page__skip player-page__skip--intro" onClick={() => skipWindow(activeMarkers.intro)}>
            Passer l&apos;intro
          </button>
        ) : null}

        {showNextPanel && nextPlayable ? (
          <div className="player-page__next">
            <p>Episode suivant</p>
            <h3>{nextPlayable.title}</h3>
            <span>Lecture automatique dans {countdown}s</span>
            <div className="player-page__nextActions">
              <button onClick={jumpToNext}>
                <SkipNext /> Lire maintenant
              </button>
              <button onClick={() => setShowNextPanel(false)}>Plus tard</button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="player-page__details">
        <article className="player-page__summary player-page__summary--premium">
          <div className="player-page__headline">
            <p className="player-page__eyebrow">{activeEpisodeLabel || title?.categoryLabel || title?.type}</p>
            <h2>{activeAsset?.title || title?.title}</h2>
          </div>

          <p className="player-page__storyline">
            {activeAsset?.description || title?.description || "Une selection premium a regarder maintenant."}
          </p>

          <div className="player-page__meta">
            <span>{title?.year || "Catalogue"}</span>
            {activeAsset?.durationLabel ? <span>{activeAsset.durationLabel}</span> : null}
            {title?.categoryLabel ? <span>{title.categoryLabel}</span> : null}
            {captionLabels.length ? (
              <span className="player-page__metaIcon">
                <ClosedCaptionOutlined /> {captionLabels.join(", ")}
              </span>
            ) : null}
            <span className="player-page__metaIcon">
              <SettingsOutlined /> Lecture adaptative
            </span>
          </div>
        </article>

        <aside className="player-page__watchCard">
          <span className="player-page__watchKicker">Lecture active</span>
          <strong>{activeEpisodeLabel || "Titre principal"}</strong>
          <div className="player-page__watchProgress">
            <span style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
          </div>
          <div className="player-page__facts">
            {detailFacts.map((fact) => (
              <div key={fact.label}>
                <span>{fact.label}</span>
                <strong>{fact.value}</strong>
              </div>
            ))}
          </div>
          <div className="player-page__social">
            <LikeDislike contentId={title?.id} userParamId={user?.userId} />
          </div>
        </aside>
      </div>

      {visibleEpisodes.length ? (
        <section className="player-page__episodes">
          <div className="player-page__sectionHeader">
            <div>
              <p>Lecture continue</p>
              <h3>Episodes disponibles</h3>
            </div>
          </div>
          <div className="player-page__episodeRail">
            {visibleEpisodes.map((episode) => (
              <Link
                key={episode.id}
                to={getDirectPlayPath(title, episode.id)}
                className={`player-page__episodeChip ${
                  String(episode.id) === String(episodeId || activeAsset?.id) ? "player-page__episodeChip--active" : ""
                }`}
              >
                <div className="player-page__episodeThumb">
                  <img src={buildMediaUrl(episode.imagePath || episode.image || title?.backdropPath)} alt={episode.title} />
                  {episode.durationLabel ? <span>{episode.durationLabel}</span> : null}
                </div>
                <div className="player-page__episodeBody">
                  <span>
                    S{episode.seasonNumber}:E{episode.episodeNumber}
                  </span>
                  <strong>{episode.title}</strong>
                  {episode.description ? <p>{episode.description}</p> : null}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {recommendations.length ? (
        <section className="player-page__recommendations">
          <div className="player-page__sectionHeader">
            <div>
              <p>Selection intelligente</p>
              <h3>Videos suivantes et suggestions</h3>
            </div>
          </div>
          <div className="player-page__recommendationGrid">
            {recommendations.map((item) => (
              <Link key={item.id} to={getDirectPlayPath(item)} className="player-page__recommendationCard">
                <img src={buildMediaUrl(item.backdropPath || item.posterPath)} alt={item.title} />
                <div className="player-page__recommendationOverlay">
                  <span>{item.type === "film" ? "Film" : item.type === "serie" ? "Serie" : "Doc"}</span>
                  <strong>{item.title}</strong>
                  <small>{item.categoryLabel || item.year || "Suggestion"}</small>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {loading ? <div className="player-page__loading">Chargement du lecteur...</div> : null}
    </div>
  );
}

export default VideoPlayer;
